/**
 * Breadcrumb item structure
 */
export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Generates Breadcrumb schema from an array of breadcrumb items
 */
export function generateBreadcrumbSchema(
  breadcrumbs: BreadcrumbItem[]
) {
  const itemListElement = breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

/**
 * Generates breadcrumbs automatically from URL path
 * Example: /products/ring/sizes -> [Home, Products, Ring, Sizes]
 */
export function generateBreadcrumbsFromPath(
  pathname: string,
  baseUrl = 'https://www.ultrahuman.com'
): BreadcrumbItem[] {
  // Always start with home
  const breadcrumbs: BreadcrumbItem[] = [
    {
      name: 'Home',
      url: baseUrl,
    },
  ];

  // Split path and filter empty strings
  const pathParts = pathname.split('/').filter((part) => part !== '');

  // Build breadcrumbs from path segments
  let currentPath = baseUrl;
  pathParts.forEach((part) => {
    currentPath += `/${part}`;

    // Convert kebab-case or snake_case to Title Case
    const name = part
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    breadcrumbs.push({
      name,
      url: currentPath,
    });
  });

  return breadcrumbs;
}

/**
 * Generates breadcrumbs with custom name mapping
 * Useful when you want specific names instead of auto-generated ones
 */
export function generateBreadcrumbsFromPathWithMapping(
  pathname: string,
  nameMapping: Record<string, string>,
  baseUrl = 'https://www.ultrahuman.com'
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      name: 'Home',
      url: baseUrl,
    },
  ];

  const pathParts = pathname.split('/').filter((part) => part !== '');

  let currentPath = baseUrl;
  pathParts.forEach((part) => {
    currentPath += `/${part}`;

    // Use mapping if available, otherwise convert to Title Case
    const name =
      nameMapping[part] ||
      part
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    breadcrumbs.push({
      name,
      url: currentPath,
    });
  });

  return breadcrumbs;
}

/**
 * Converts the breadcrumb schema to JSON-LD string for injection
 */
export function getBreadcrumbSchemaScript(
  breadcrumbs: BreadcrumbItem[]
): string {
  return JSON.stringify(generateBreadcrumbSchema(breadcrumbs));
}

/**
 * Generates breadcrumb schema script directly from pathname
 */
export function getBreadcrumbSchemaScriptFromPath(
  pathname: string,
  baseUrl?: string
): string {
  const breadcrumbs = generateBreadcrumbsFromPath(pathname, baseUrl);
  return getBreadcrumbSchemaScript(breadcrumbs);
}

/**
 * Common breadcrumb name mappings for Ultrahuman pages
 */
export const ULTRAHUMAN_BREADCRUMB_NAMES: Record<string, string> = {
  ring: 'Ultrahuman Ring',
  'ring-air': 'Ring AIR',
  m1: 'M1 CGM',
  blood: 'Blood Testing',
  home: 'Home Devices',
  'sleep-mask': 'Sleep Mask',
  'air-us': 'Ring AIR (US)',
  'air-uk': 'Ring AIR (UK)',
  'air-eu': 'Ring AIR (EU)',
  'ring-us': 'Ring (US)',
  'ring-uk': 'Ring (UK)',
  'ring-eu': 'Ring (EU)',
  blog: 'Blog',
  science: 'Science',
  support: 'Support',
  about: 'About Us',
  contact: 'Contact',
  faq: 'FAQ',
  'vs-oura': 'Ultrahuman vs Oura',
  'vs-whoop': 'Ultrahuman vs Whoop',
  'vs-apple-watch': 'Ultrahuman vs Apple Watch',
  'vs-samsung': 'Ultrahuman vs Samsung',
};
